import { memo, useCallback } from 'react'

import { Check, Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, FormControl, InputLabel, MenuItem, OutlinedInput, Select, TextField, Typography } from '@mui/material'
import {
  OrganizationRoleRamMemberInsert,
  OrganizationRoleRamMemberRolesEnum,
  OrganizationsOrganizationIdMembersMemberIdGetRequest,
} from '@nativewaves/nw-cloud-sdk/org'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import SimpleBar from 'simplebar-react'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { appearance } from '@shared/utils/support'
import { useOrganizationMemberCreateMutation } from 'hooks/mutations/org'
import { usersOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { OrgAPI } from 'services/api'
import { organizationQueryKeys } from 'services/queryKeys'
import { stopPropagate } from 'utils'

type FormStructure = Omit<OrganizationRoleRamMemberInsert, 'roles'> & {
  roles: OrganizationRoleRamMemberRolesEnum[]
}

const UserDialogContentRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()
  const { enqueueSnackbar } = useSnackbar()

  const { organizationId } = usersOverviewRoute.useParams()

  const { data: organization } = useQuery(organizationQueryKeys.detail({ organizationId }))

  const { mutate: addMember, ...addMemberMutation } = useOrganizationMemberCreateMutation()
  const memberGetMutation = useMutation({
    mutationFn: (params: OrganizationsOrganizationIdMembersMemberIdGetRequest) =>
      OrgAPI.organizations.organizationsOrganizationIdMembersMemberIdGet(params),
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
  })

  const {
    control,
    handleSubmit,
    setError: setFormError,
  } = useForm<FormStructure>({
    defaultValues: {
      roles: [],
    },
  })

  const handleFormSubmit = useCallback(
    async (data: FormStructure) => {
      if (!data.userId) {
        throw new Error(t('domain:My.Organization.userIdNotSet'))
      }

      try {
        const existingUser = await memberGetMutation.mutateAsync({ organizationId, memberId: `user-${data.userId}` })
        if (existingUser) {
          enqueueSnackbar(t('domain:My.Organization.userIsAlreadyAssigned'), {
            variant: 'error',
          })
          setFormError('userId', { message: t('domain:My.Organization.userIsAlreadyAssigned') }, { shouldFocus: true })
          return
        }
      } catch (_) {}

      addMember(
        {
          organizationId,
          organizationRoleRamMemberInsert: {
            ...data,
            // TODO: Disable Sets in API Sdk, since they are not handled properly
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            roles: data.roles,
          },
        },
        {
          onSuccess: () => {
            setTimeout(() => {
              handleClose()
            }, 300)
          },
          onError: () => {
            setFormError(
              'userId',
              { message: t('domain:My.Organization.userIsAlreadyAssigned') },
              { shouldFocus: true },
            )
          },
        },
      )
    },
    [t, addMember, organizationId, memberGetMutation, setFormError, enqueueSnackbar, handleClose],
  )

  return (
    <DialogFormContent
      sx={{ minWidth: 480 }}
      title={`${t('domain:My.Organization.associatePeopleWith')} ${organization?.name}`}
      description={t('domain:My.Organization.addUserInstructions')}
      onSubmit={stopPropagate(handleSubmit(handleFormSubmit))}
      Illustration={null}
      SaveButton={
        <AnimatePresence mode="wait">
          <LoadingButton
            sx={{ ml: 'auto' }}
            key="e-mail"
            component={motion.button}
            variants={appearance}
            initial="hidden"
            animate="visible"
            exit="hidden"
            endIcon={addMemberMutation.isSuccess ? <Check /> : <Save />}
            loading={addMemberMutation.isPending}
            disabled={addMemberMutation.isSuccess}
            loadingPosition="end"
            type="submit"
            variant="text"
          >
            Save
          </LoadingButton>
        </AnimatePresence>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, mt: 3 }}>
        <Controller
          control={control}
          name="userId"
          rules={{
            required: true,
          }}
          render={({ field, fieldState, formState }) => (
            <TextField
              sx={{ flex: 2 }}
              {...field}
              error={!!fieldState.error}
              label={t('domain:My.Organization.userId')}
              required
              fullWidth
              InputProps={{
                endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />,
              }}
            />
          )}
        />
        <Typography sx={{ flex: 0 }}>{t('domain:My.Organization.has')}</Typography>

        <Controller
          control={control}
          name="roles"
          render={({ field, fieldState, formState }) => (
            <FormControl sx={{ flex: 1 }} required>
              <InputLabel id="roles">{t('domain:My.Organization.assignedRoles')}</InputLabel>
              <Select
                {...field}
                multiple
                labelId="roles"
                input={
                  <OutlinedInput
                    label={t('domain:My.Organization.assignedRoles')}
                    endAdornment={
                      <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                    }
                  />
                }
                renderValue={(selected) => (
                  <SimpleBar>
                    <Box sx={{ display: 'flex', columnGap: 1 }}>
                      {selected.map((value) => (
                        <CodeInline sx={{ py: 0 }} key={value}>
                          {value}
                        </CodeInline>
                      ))}
                    </Box>
                  </SimpleBar>
                )}
              >
                {Object.values(OrganizationRoleRamMemberRolesEnum).map((role) => (
                  <MenuItem key={role} value={role} selected={field.value.includes(role)}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const UserDialogContent = memo(UserDialogContentRaw)
